import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

const SUGGESTED = {
  legal: [
    "Which contracts auto-renew in the next 90 days and what is our combined exposure?",
    "Show every IT contract with an arbitration clause.",
    "List contracts over $250K that are unbudgeted.",
    "Which vendors have more than one active contract?",
  ],
  director: [
    "What am I spending annually with GE HealthCare?",
    "Summarize my contracts expiring this fiscal year.",
    "Which of my contracts have high-severity red flags?",
    "How many active contracts do I have?",
  ],
};

function renderMdSimple(md) {
  if (!md) return null;
  return md.split("\n").map((l, i) => {
    if (l.startsWith("## ")) return <h3 key={i} className="text-sm font-bold text-[#005289] mt-4 mb-2">{l.slice(3)}</h3>;
    if (l.startsWith("| ") && l.includes("|")) return <div key={i} className="font-mono text-xs bg-slate-50 px-2 py-1">{l}</div>;
    if (l.startsWith("- ")) return <li key={i} className="text-sm text-slate-700 ml-4 list-disc">{l.slice(2)}</li>;
    return <p key={i} className="text-sm text-slate-700 leading-relaxed">{l}</p>;
  });
}

export default function AIAssistant() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);

  const isDirector = user?.role === "department_director" || user?.role === "business_unit_rep";
  const suggested = isDirector ? SUGGESTED.director : SUGGESTED.legal;

  const send = async (text) => {
    const question = text ?? q;
    if (!question.trim()) return;
    setBusy(true);
    setMessages(prev => [...prev, { role: "user", text: question }]);
    setQ("");
    try {
      const { data } = await api.post("/ai/query", {
        question,
        scope: isDirector ? "department" : "all",
        department: isDirector ? user.department : undefined,
      });
      setMessages(prev => [...prev, { role: "assistant", text: data.answer, records: data.records_used }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI query failed");
      setMessages(prev => [...prev, { role: "assistant", text: "Error: " + (e?.response?.data?.detail || e.message) }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="ai-page">
      <div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#005289]" />
          <h1 className="text-2xl font-bold text-slate-900">AI Assistant</h1>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {isDirector
            ? `Scoped to your department (${user?.department}). Ask about your contracts, spend, or renewals.`
            : "Natural-language reporting across all HCD contracts. Powered by Claude Sonnet 5."}
        </p>
      </div>

      {messages.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Try asking</div>
          <div className="grid grid-cols-2 gap-2">
            {suggested.map((s, i) => (
              <button key={i} onClick={() => send(s)} className="text-left px-3 py-2.5 text-xs border border-slate-200 rounded-md hover:bg-slate-50 hover:border-[#005289]" data-testid={`suggest-${i}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`p-4 rounded-md border ${m.role === "user" ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200"}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{m.role === "user" ? "You" : "HCD AI"}</div>
            {m.role === "user" ? <div className="text-sm text-slate-900">{m.text}</div> : <div>{renderMdSimple(m.text)}</div>}
            {m.records != null && <div className="text-[10px] text-slate-400 mt-2">Analyzed {m.records} contract record(s)</div>}
          </div>
        ))}
        {busy && <div className="text-xs text-slate-500 flex items-center gap-2"><Sparkles className="w-3 h-3 animate-pulse" /> Analyzing...</div>}
      </div>

      <div className="sticky bottom-0 pt-3 bg-slate-50">
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md p-2">
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
                placeholder="Ask about contracts, renewals, spend, red flags..." data-testid="ai-input"
                className="flex-1 h-9 px-3 text-sm border-none focus:outline-none" />
          <button onClick={() => send()} disabled={busy || !q.trim()}
                  className="h-9 px-4 text-white rounded-md text-xs font-semibold disabled:opacity-50 flex items-center gap-1.5"
                  style={{ background: "#005289" }} data-testid="ai-send">
            <Send className="w-3.5 h-3.5" /> Ask
          </button>
        </div>
      </div>
    </div>
  );
}
